/**
 * Pure site search over the entries already loaded for the map: matches on site id or
 * name, ignoring case, accents and surrounding whitespace, best match first.
 */

import type { SiteMapEntry } from '../models/siteMapModels';

export const SITE_SEARCH_LIMIT = 8;

export interface SiteSearchResult {
  /** At most the requested limit, best first */
  readonly matches: readonly SiteMapEntry[];
  /** Every match, so the UI can say there are more than shown */
  readonly total: number;
}

/** How well an entry matches; lower is better. */
const Rank = {
  ExactId: 0,
  ExactName: 1,
  IdPrefix: 2,
  NamePrefix: 3,
  NameWordPrefix: 4,
  Contains: 5,
} as const;

type Rank = (typeof Rank)[keyof typeof Rank];

interface RankedEntry {
  readonly entry: SiteMapEntry;
  readonly rank: Rank;
}

const COMBINING_MARKS = /\p{M}/gu;
const WHITESPACE = /\s+/g;
/** A word starts at the beginning of the text or after a character matching this. */
const WORD_SEPARATOR = /[^\p{L}\p{N}]/u;

/** Lower case, accents removed, whitespace trimmed and collapsed. */
export function normaliseSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .trim()
    .replace(WHITESPACE, ' ');
}

function hasWordStartingWith(text: string, query: string): boolean {
  let from = text.indexOf(query);
  while (from !== -1) {
    if (from === 0 || WORD_SEPARATOR.test(text.charAt(from - 1))) return true;
    from = text.indexOf(query, from + 1);
  }
  return false;
}

function rankOf(entry: SiteMapEntry, query: string): Rank | null {
  const id = normaliseSearchText(entry.site.siteId);
  const name = normaliseSearchText(entry.site.name);
  if (id === query) return Rank.ExactId;
  if (name === query) return Rank.ExactName;
  if (id.startsWith(query)) return Rank.IdPrefix;
  if (name.startsWith(query)) return Rank.NamePrefix;
  if (hasWordStartingWith(name, query)) return Rank.NameWordPrefix;
  if (id.includes(query) || name.includes(query)) return Rank.Contains;
  return null;
}

function compareRanked(a: RankedEntry, b: RankedEntry): number {
  return (
    a.rank - b.rank ||
    a.entry.site.name.localeCompare(b.entry.site.name) ||
    a.entry.site.siteId.localeCompare(b.entry.site.siteId)
  );
}

export function searchSites(
  entries: readonly SiteMapEntry[],
  query: string,
  limit: number = SITE_SEARCH_LIMIT
): SiteSearchResult {
  const normalisedQuery = normaliseSearchText(query);
  if (normalisedQuery === '') return { matches: [], total: 0 };

  const ranked = entries
    .flatMap((entry): readonly RankedEntry[] => {
      const rank = rankOf(entry, normalisedQuery);
      return rank === null ? [] : [{ entry, rank }];
    })
    .sort(compareRanked);

  return {
    matches: ranked.slice(0, limit).map(({ entry }) => entry),
    total: ranked.length,
  };
}
